import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  Announcements,
  CollisionDetection,
  DropAnimation,
  KeyboardCoordinateGetter,
  MeasuringConfiguration,
  Modifiers,
  PointerActivationConstraint,
  ScreenReaderInstructions,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import type {
  AnimateLayoutChanges,
  NewIndexGetter,
  SortingStrategy,
} from '@dnd-kit/sortable';
import { subtract } from '@dnd-kit/utilities';
import type { TWidget } from 'types/hubDashboard.type';
import Item from './SortableItem.component';

export interface SortableProps {
  activationConstraint?: PointerActivationConstraint;
  animateLayoutChanges?: AnimateLayoutChanges;
  adjustScale?: boolean;
  collisionDetection?: CollisionDetection;
  Container?: React.ElementType;
  dropAnimation?: DropAnimation | null;
  getNewIndex?: NewIndexGetter;
  itemCount?: number;
  items: TWidget[];
  measuring?: MeasuringConfiguration;
  modifiers?: Modifiers;
  onRemove: ({
    widgetId,
    index,
  }: {
    widgetId: UniqueIdentifier;
    index: number;
  }) => void;
  onEdit: ({
    widgetId,
    index,
  }: {
    widgetId: UniqueIdentifier;
    index: number;
  }) => void;
  reorderItems(activeIndex: number, overIndex: number): void;
  strategy?: SortingStrategy;
  useDragOverlay?: boolean;
  getItemStyles?(args: {
    id: UniqueIdentifier;
    index: number;
    isSorting: boolean;
    isDragOverlay: boolean;
    overIndex: number;
    isDragging: boolean;
  }): React.CSSProperties;
  isDisabled?(id: UniqueIdentifier): boolean;
}

const screenReaderInstructions: ScreenReaderInstructions = {
  draggable: `
    To pick up a sortable item, press the space bar.
    While sorting, use the arrow keys to move the item.
    Press space again to drop the item in its new position, or press escape to cancel.
  `,
};

const coordinateGetter: KeyboardCoordinateGetter = (event, args) => {
  const {
    context: {
      active,
      collisionRect,
      droppableRects,
      droppableContainers,
      over,
    },
  } = args;

  event.preventDefault();

  if (!active || !collisionRect || !over) {
    return;
  }

  if (!over.data.current) return;

  let droppableTop = 0;
  let columns = 1;
  const enabledContainers = droppableContainers.getEnabled();
  enabledContainers.forEach((entry, index) => {
    if (!entry.rect.current) return;

    const { top } = entry.rect.current;
    if (index === 0) {
      droppableTop = top;
      return;
    }

    if (top !== droppableTop) return;

    if (top === droppableTop) {
      columns++;
    }
  });

  const { index: currentLocation } = over.data.current.sortable as {
    index: number;
  };
  let newLocation = currentLocation;
  switch (event.code) {
    case 'ArrowDown':
      newLocation += columns;

      break;

    case 'ArrowUp':
      newLocation -= columns;

      break;

    case 'ArrowLeft':
      newLocation--;
      if (newLocation % columns >= columns - 1) {
        return;
      }

      break;

    case 'ArrowRight':
      newLocation++;
      if (newLocation % columns <= 0) {
        return;
      }

      break;
  }

  if (newLocation < 0) return;
  if (newLocation > enabledContainers.length - 1) return;

  const newContainer = enabledContainers[newLocation];
  const newEnabled = newContainer && !newContainer.disabled;

  if (!newEnabled) return;

  const newDroppable = droppableContainers.get(newContainer.id);
  if (!newDroppable) return;

  const newNode = newDroppable.node.current;
  if (!newNode) return;

  const newRect = droppableRects.get(newDroppable.id);
  if (!newRect) return;

  const offset = {
    x: Math.round(collisionRect.width - newRect.width),
    y: Math.round(collisionRect.height - newRect.height),
  };

  const rectCoordinates = {
    x: Math.round(newRect.left),
    y: Math.round(newRect.top),
  };

  const newCoordinates =
    offset.x && offset.y ? rectCoordinates : subtract(rectCoordinates, offset);

  return newCoordinates;
};

export function Sortable({
  activationConstraint,
  animateLayoutChanges,
  Container,
  collisionDetection = closestCenter,
  getNewIndex,
  isDisabled = () => false,
  items: initialItems,
  measuring,
  onRemove,
  onEdit,
  modifiers,
  reorderItems,
  strategy = rectSortingStrategy,
}: SortableProps) {
  const items = useMemo(() => {
    return initialItems.map((widget, index) => {
      const { _id } = widget;
      return {
        id: _id || index,
        widget,
      };
    });
  }, [initialItems]);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint,
    }),
    useSensor(TouchSensor, {
      activationConstraint,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    })
  );

  const isFirstAnnouncement = useRef(true);
  const getIndex = (id: UniqueIdentifier) =>
    items.findIndex(({ id: itemId }) => itemId === id);
  const getPosition = (id: UniqueIdentifier) => getIndex(id) + 1;
  const activeIndex = activeId != null ? getIndex(activeId) : -1;
  const announcements: Announcements = {
    onDragStart({ active: { id } }) {
      return `Picked up sortable item ${String(
        id
      )}. Sortable item ${id} is in position ${getPosition(id)} of ${
        items.length
      }`;
    },
    onDragOver({ active, over }) {
      // In this specific use-case, the picked up item's `id` is always the same as the first `over` id.
      // The first `onDragOver` event therefore doesn't need to be announced, because it is called
      // immediately after the `onDragStart` announcement and is redundant.
      if (isFirstAnnouncement.current === true) {
        isFirstAnnouncement.current = false;
        return;
      }

      if (over) {
        return `Sortable item ${
          active.id
        } was moved into position ${getPosition(over.id)} of ${items.length}`;
      }

      return;
    },
    onDragEnd({ active, over }) {
      if (over) {
        return `Sortable item ${
          active.id
        } was dropped at position ${getPosition(over.id)} of ${items.length}`;
      }

      return;
    },
    onDragCancel({ active: { id } }) {
      return `Sorting was cancelled. Sortable item ${id} was dropped and returned to position ${getPosition(
        id
      )} of ${items.length}.`;
    },
  };

  useEffect(() => {
    if (activeId == null) {
      isFirstAnnouncement.current = true;
    }
  }, [activeId]);

  return (
    <DndContext
      accessibility={{
        announcements,
        screenReaderInstructions,
      }}
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }

        setActiveId(active.id);
      }}
      onDragEnd={({ over }) => {
        setActiveId(null);

        if (over) {
          const overIndex = getIndex(over.id);
          if (activeIndex !== overIndex) {
            reorderItems(activeIndex, overIndex);
          }
        }
      }}
      onDragCancel={() => setActiveId(null)}
      measuring={measuring}
      modifiers={modifiers}
    >
      <SortableContext items={items} strategy={strategy}>
        <Container>
          {items.map(({ id, widget }, index) => (
            <SortableItem
              key={id}
              id={id}
              index={index}
              widget={widget}
              disabled={isDisabled(index)}
              onRemove={onRemove}
              onEdit={onEdit}
              animateLayoutChanges={animateLayoutChanges}
              getNewIndex={getNewIndex}
            />
          ))}
        </Container>
      </SortableContext>
      {createPortal(
        <>
          <DragOverlay>
            {activeId !== null && (
              <Item
                id={activeId}
                widget={items[activeIndex].widget}
                dragOverlay
              />
            )}
          </DragOverlay>
        </>,
        document.body
      )}
    </DndContext>
  );
}

interface SortableItemProps {
  animateLayoutChanges?: AnimateLayoutChanges;
  disabled?: boolean;
  getNewIndex?: NewIndexGetter;
  id: UniqueIdentifier;
  widget: TWidget;
  index: number;
  useDragOverlay?: boolean;
  onEdit: ({
    widgetId,
    index,
  }: {
    widgetId: UniqueIdentifier;
    index: number;
  }) => void;
  onRemove: ({
    widgetId,
    index,
  }: {
    widgetId: UniqueIdentifier;
    index: number;
  }) => void;
}

export function SortableItem({
  disabled,
  animateLayoutChanges,
  getNewIndex,
  widget,
  id,
  onEdit,
  index,
  onRemove,
}: SortableItemProps) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges,
    disabled,
    getNewIndex,
  });

  return (
    <Item
      ref={setNodeRef}
      dragging={isDragging}
      activatorRef={setActivatorNodeRef}
      index={index}
      id={id}
      onEdit={onEdit}
      onRemove={onRemove}
      transform={transform}
      widget={widget}
      transition={transition}
      listeners={listeners}
      data-index={index}
      data-id={id}
      {...attributes}
    />
  );
}
